<template>
  <v-row>
    <v-col cols="8" offset-lg="2" offset-md="2">
      <Breadcrumbs
        class="ml-n3"
        :items="[
          {
            text: 'All Branch Managers',
            disabled: false,
            href: '/app/branch-managers'
          },
          {
            text: `${branchName} Branch`,
            disabled: true
          },
          {
            text: `${user.name}`,
            disabled: true
          }
        ]"
      />
      <div class="d-flex flex-column mb-3 ml-1 mt-n4">
        <PageTitle
          text="Branch Manager"
          class=" ft font-weight-bold font-size-lg mb-4"
        />
      </div>
      <div v-if="isListLoading" class="d-flex flex-column mx-2 flex-grow-1">
        <v-progress-circular color="primary" indeterminate />
      </div>
      <div v-else class="d-flex flex-column mx-2 flex-grow-1">
        <div
          class="d-flex flex-column flex-grow-1 py-3 px-4 grey lighten-4 rounded-lg"
        >
          <h2 class="ft font-weight-medium font-size-lg text-uppercase pb-2">
            Profile
          </h2>
          <div
            class="d-flex flex-column flex-grow-1 white box-shadow pa-5 rounded-lg"
          >
            <ValidationObserver v-slot="{ handleSubmit }">
              <form @submit.prevent="handleSubmit(updateProfileDetails)">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-text-field
                    label="Name of User"
                    outlined
                    v-model="user.name"
                    hint="Enter the name of the user"
                    class="ft font-weight-medium font-size-md"
                    :error-messages="errors[0]"
                  />
                </ValidationProvider>
                <div class="d-flex flex-row flex-grow-1">
                  <ValidationProvider
                    tag="div"
                    class="d-flex flex-row flex-grow-1"
                    rules="required|email"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      label="Email Address"
                      outlined
                      v-model="user.email"
                      :hint="`Enter the email address of the user`"
                      :error-messages="errors[0]"
                      class="ft font-weight-medium font-size-md mr-1"
                    />
                  </ValidationProvider>
                  <ValidationProvider tag="div" class="d-flex flex-grow-1">
                    <v-text-field
                      label="Username"
                      v-model="user.username"
                      outlined
                      :hint="`Enter your username here`"
                      class="ft font-weight-medium font-size-md ml-1"
                    />
                  </ValidationProvider>
                </div>
                <div class="d-flex flex-row flex-grow-1">
                  <ValidationProvider
                    tag="div"
                    class="d-flex flex-row flex-grow-1"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      label="Mobile Phone Number"
                      outlined
                      v-model="user.mobile"
                      :hint="`Enter the phone number of the user`"
                      :error-messages="errors[0]"
                      class="ft font-weight-medium font-size-md mr-1"
                    />
                  </ValidationProvider>
                </div>
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-autocomplete
                    :items="roles"
                    :error-messages="errors[0]"
                    item-value="id"
                    v-model="user.role"
                    item-text="name"
                    outlined
                    label="Assign Branch Role To A User"
                    color="primary"
                    class="ft font-weight-medium font-size-sm"
                  >
                    <template #item="{item}">
                      <span class="ft font-weight-medium font-size-sm">
                        {{ item.name }}
                      </span>
                    </template>
                  </v-autocomplete>
                </ValidationProvider>
                <v-checkbox v-model="user.isActive" class="mt-n3">
                  <template v-slot:label>
                    <div class="ft font-weight-medium font-size-md">
                      By checking this box, you
                      <strong class="font-weight-bold text--black">{{
                        user.isActive ? "deactivate" : "activate"
                      }}</strong>
                      this current user
                    </div>
                  </template>
                </v-checkbox>

                <v-spacer />
                <div
                  class="d-flex flex-row flex-grow-1 mt-3"
                  style="justify-content: flex-end;"
                >
                  <v-btn
                    color="primary"
                    type="submit"
                    :disabled="loading"
                    :loading="loading"
                  >
                    <span class="ft font-weight-medium font-size-sm"
                      >Update details</span
                    >
                  </v-btn>
                </div>
              </form>
            </ValidationObserver>
          </div>
        </div>
        <div
          class="d-flex flex-column mt-3 flex-grow-1 py-3 px-4 grey lighten-4 rounded-lg"
        >
          <EmptyPage
            title="Reset Branch Manager's Password"
            :image="require('@/assets/account-recovery.png')"
            :subTitle="
              `Has ${user.name} forgotten their password? Don't fret, send a temporary password to their phone number by clicking the button below.`
            "
          >
            <template #action>
              <div
                class="d-flex flex-column flex-grow-1 justify-center align-center py-2"
              >
                <v-btn
                  small
                  :disabled="
                    isLoading || resetTimer !== 30 || generatePasswordLoading
                  "
                  :loading="isLoading"
                  color="primary"
                  @click.stop="sendTemporaryPassword"
                >
                  {{
                    resetTimer !== 30
                      ? `Resend temporary password in ${resetTimer} sec ...`
                      : "Send temporary password"
                  }}
                </v-btn>
              </div>
              <div
                class="d-flex flex-column flex-grow-1 justify-center align-center pa-2"
              >
                <h3
                  class="ft font-weight-medium text-uppercase font-size-md py-2"
                >
                  OR
                </h3>
                <p class="ft font-weight-medium font-size-sm">
                  Generate a temporary password and share it with the user
                </p>
                <v-text-field
                  placeholder="Generate a password .."
                  dense
                  outlined
                  readonly
                  :value="temporaryPassword"
                  hide-details
                  filled
                >
                  <template v-slot:append>
                    <v-progress-circular
                      v-if="generatePasswordLoading"
                      size="24"
                      width="2"
                      color="primary"
                      indeterminate
                    ></v-progress-circular>
                    <i
                      @click="copyToClipboard"
                      class="material-icons-outlined font-size-md mt-1"
                      style="cursor: pointer"
                      >content_copy</i
                    >
                  </template>
                  <template v-slot:append-outer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          color="primary"
                          fab
                          small
                          :loading="generatePasswordLoading"
                          @click="
                            $store.dispatch(
                              'users/generateTemporaryPassword',
                              $route.params.id
                            )
                          "
                          class="mt-n2"
                          v-on="on"
                          v-bind="attrs"
                        >
                          <i class="material-icons-outlined">lock_reset</i>
                        </v-btn>
                      </template>
                      <span class="ft font-weight-medium font-size-sm"
                        >Generate password</span
                      >
                    </v-tooltip>
                  </template>
                </v-text-field>
              </div>
            </template>
          </EmptyPage>
        </div>
        <div
          class="d-flex flex-column mt-3 mb-6 flex-grow-1 py-3 px-4 grey lighten-4 rounded-lg"
          style="border: 2px dashed red !important;"
        >
          <EmptyPage
            :title="
              `Transfer ${user.name} from ${branchName} to another branch`
            "
            :image="require('@/assets/send-money.png')"
          >
            <template #action>
              <div
                class="d-flex flex-row flex-grow-1 justify-center align-center flex-wrap-reverse"
              >
                <div class="d-flex flex-column">
                  <v-text-field
                    outlined
                    readonly
                    hide-details
                    label="Existing branch"
                    color="primary"
                    :value="branchName"
                    dense
                    class="ft font-weight-medium font-size-sm"
                  />
                </div>
                <div class="d-flex flex-column mx-2">
                  <i class="material-icons-outlined">arrow_forward</i>
                </div>
                <div class="d-flex flex-column">
                  <v-autocomplete
                    :items="
                      branches.filter(
                        branch => branch.id !== $route.query.branchId
                      )
                    "
                    dense
                    item-value="id"
                    v-model="chosenBranch"
                    item-text="name"
                    return-object
                    outlined
                    hide-details
                    label="Assign to new branch"
                    color="primary"
                    class="ft font-weight-medium font-size-sm"
                  >
                    <template #item="{item}">
                      <span class="ft font-weight-medium font-size-sm">
                        {{ item.name }}
                      </span>
                    </template>
                  </v-autocomplete>
                </div>
                <div class="d-flex column  mx-2">
                  <v-btn
                    :disabled="branchMangerTransfers.to.branchId === null"
                    @click.stop="open('alert')"
                    fab
                    color="primary"
                    small
                  >
                    <i class="material-icons-outlined">check</i>
                  </v-btn>
                </div>
              </div>
            </template>
          </EmptyPage>
        </div>
      </div>
    </v-col>
    <SnackBar
      :showSnackBar="showSnackBar"
      :message="message"
      :status="status"
    />
    <BranchTransferAlertDialog
      :state="dialog('alert')"
      :name="user.name"
      :from="branchName"
      :is-loading="isLoading"
      :to="branchMangerTransfers.to.name"
      @proceed="confirmManagerTransfer"
      @close="close"
    />
  </v-row>
</template>
<script>
import PageTitle from "../../../components/PageTitle";
import DialogMixins from "@/mixins/DialogMixins";
import { mapGetters } from "vuex";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { email, numeric, required } from "vee-validate/dist/rules";
import SnackBarMixins from "@/mixins/SnackBarMixins";
import BranchTransferAlertDialog from "@/components/BranchTransferAlertDialog.vue";
extend("required", {
  ...required,
  message: "Field is required"
});

extend("email", {
  ...email,
  message: `Input must be a valid email address`
});

extend("numeric", {
  ...numeric,
  message: "Field accept only numbers"
});
export default {
  name: "BranchManagers",
  components: {
    BranchTransferAlertDialog,
    ValidationProvider,
    PageTitle,
    ValidationObserver,
    SnackBar: () => import(`../../../components/SnackBar`),
    Breadcrumbs: () => import(`../../../components/Breadcrumbs`),
    EmptyPage: () => import(`../../../components/EmptyPage`)
  },
  mixins: [DialogMixins, SnackBarMixins],
  computed: {
    ...mapGetters({
      userDetails: "users/getUserDetails",
      isLoading: "getIsLoading",
      isPageLoading: "getIsPageLoading",
      isListLoading: "getIsListLoading",
      loading: "getIsLoading",
      generatePasswordLoading: "getIsPasswordGenerateLoading",
      roles: "acl/getRoleList",
      resetTimer: "auth/getCurrentResetPasswordTimer",
      branches: "branches/getAllBranches",
      temporaryPassword: "users/getTemporaryPassword"
    })
  },
  watch: {
    userDetails(value) {
      const {
        id,
        name,
        email,
        mobile,
        role,
        username,
        isActive,
        ...rest
      } = value;
      this.branchName = rest.branchId.name;
      this.user = {
        id,
        name,
        email,
        mobile,
        role,
        username,
        isActive,
        ...(rest.userSMSNotificationPreference && {
          preferences: rest.userSMSNotificationPreference
        })
      };
      this.branchMangerTransfers = {
        userId: this.user.id,
        from: {
          branchId: this.$route.query.branchId,
          name: this.branchName
        },
        to: {
          branchId: null
        }
      };
    },
    chosenBranch(value) {
      this.branchMangerTransfers.to = {
        branchId: value.id,
        name: value.name
      };
    }
  },
  data() {
    return {
      branchName: "",
      chosenBranch: "",
      user: {
        name: "",
        email: "",
        mobile: "",
        role: null,
        username: "",
        isActive: false
      },
      branchMangerTransfers: {
        userId: null,
        from: {
          branchId: null,
          name: null
        },
        to: {
          branchId: null,
          name: null
        }
      },
      roleId: null
    };
  },
  methods: {
    updateProfileDetails() {
      this.$store.dispatch("users/update", this.user);
    },
    sendTemporaryPassword() {
      this.$store.dispatch("auth/forgotPassword", {
        phoneNumber: this.user.mobile
      });
    },
    confirmManagerTransfer() {
      this.$store.dispatch(
        "branches/transferBranchManager",
        this.branchMangerTransfers
      );
    },
    copyToClipboard() {
      this.$copyText(this.temporaryPassword).then(() => {
        this.$store.dispatch(
          "showSnackBar",
          {
            message: "Copied to clipboard",
            status: "success",
            snackbar: true
          },
          { root: true }
        );
      });
    }
  },
  mounted() {},
  created() {
    this.$store.dispatch("users/details", { id: this.$route.params.id });
    this.$store.dispatch(
      "acl/listOfRolesByBranchId",
      this.$route.query.branchId
    );
    this.$store.dispatch("branches/list", {
      page: 1,
      limit: 12,
      isPaginated: false,
      hide_relations: true
    });
  }
};
</script>
<style scoped lang="scss">
.dashboard--view {
  flex-basis: 30%;
  &-max {
    flex-basis: 60%;
  }
}
.card--border {
  border: 1px solid #dad1d1e0;
  border-radius: 8px;
}
</style>
